<template>
  <div class="app-container">
    <div class="filter-container">
      <el-input
        v-model="listQuery.contractName"
        placeholder="合同名称"
        style="width: 200px"
        class="filter-item"
        clearable
      />
      <el-input
        v-model="listQuery.ledgerTitle"
        placeholder="台账备注"
        style="width: 200px"
        class="filter-item"
        clearable
      />
      <el-input
        v-model="listQuery.demandUnitName"
        placeholder="需求方名称"
        style="width: 200px"
        class="filter-item"
        clearable
      />
      <el-button
        type="primary"
        icon="el-icon-search"
        class="filter-item"
        @click="handleFilter"
      >
        搜索
      </el-button>
      <el-button
        v-permission="'finance:finance-list:create'"
        type="primary"
        icon="el-icon-plus"
        class="filter-item"
        style="margin-left: 10px; background-color: #009688; border-color: #009688;"
        @click="handleCreate"
      >
        新增台账
      </el-button>
    </div>

    <el-table
      v-loading="listLoading"
      :data="list"
      border
      fit
      highlight-current-row
      style="width: 100%;"
      :summary-method="getSummaries"
      show-summary
    >
      <el-table-column label="台账编号" align="center" width="150">
        <template slot-scope="scope">
          {{ scope.row.ledger_no }}
        </template>
      </el-table-column>
      <el-table-column label="合同名称" align="center" min-width="200">
        <template slot-scope="scope">
          {{ scope.row.contract_name || '-' }}
        </template>
      </el-table-column>
      <el-table-column label="需求方名称" align="center" min-width="200">
        <template slot-scope="scope">
          {{ scope.row.demand_unit_names || '-' }}
        </template>
      </el-table-column>
      <el-table-column label="台账备注" align="center" min-width="200">
        <template slot-scope="scope">
          {{ scope.row.ledger_title }}
        </template>
      </el-table-column>
      <el-table-column label="收付款笔数" align="center" width="120">
        <template slot-scope="scope">
          <span style="color: #409EFF; font-weight: bold;">{{ scope.row.item_count || 0 }}</span>
        </template>
      </el-table-column>
      <el-table-column label="总金额" align="center" width="150">
        <template slot-scope="scope">
          <span style="color: #f56c6c; font-weight: bold;">
            {{ formatAmount(scope.row.total_amount) }}
          </span>
        </template>
      </el-table-column>
      <el-table-column label="首笔时间" align="center" width="180">
        <template slot-scope="scope">
          {{ formatDate(scope.row.first_payment_date) }}
        </template>
      </el-table-column>
      <el-table-column label="末笔时间" align="center" width="180">
        <template slot-scope="scope">
          {{ formatDate(scope.row.last_payment_date) }}
        </template>
      </el-table-column>
      <el-table-column label="操作" align="center" width="320" fixed="right">
        <template slot-scope="scope">
          <el-button
            type="success"
            size="mini"
            @click="handleView(scope.row)"
          >
            查看
          </el-button>
          <el-button
            type="warning"
            size="mini"
            @click="handleViewFlows(scope.row)"
          >
            流水查看
          </el-button>
          <el-button
            v-permission="'finance:finance-list:edit'"
            type="primary"
            size="mini"
            @click="handleUpdate(scope.row)"
          >
            编辑
          </el-button>
          <el-button
            v-permission="'finance:finance-list:delete'"
            type="danger"
            size="mini"
            @click="handleDelete(scope.row)"
          >
            删除
          </el-button>
        </template>
      </el-table-column>
      <template slot="empty">
        <div class="empty-text">
          <p>暂无数据</p>
        </div>
      </template>
    </el-table>

    <pagination
      v-show="total > 0"
      :total="total"
      :page.sync="listQuery.current"
      :limit.sync="listQuery.pageSize"
      @pagination="getList"
    />

  </div>
</template>

<script>
import Pagination from '@/components/Pagination'
import { query_fund_ledgers_pagination, delete_fund_ledger } from '@/api/modules/fund-ledger'

export default {
  name: 'FinanceList',
  components: { Pagination },
  data() {
    return {
      list: [],
      total: 0,
      listLoading: false,
      allFilteredData: [], // 所有过滤后的数据（用于计算合计）
      listQuery: {
        current: 1,
        pageSize: 10,
        contractName: '', // 合同名称（模糊）
        ledgerTitle: '', // 台账备注（对应remark字段）
        demandUnitName: '' // 需求方名称
      },
    }
  },
  created() {
    this.getList()
  },
  methods: {
    // 获取列表数据
    async getList() {
      this.listLoading = true
      
      try {
        // 构建查询参数（不传 fundType，查询全部台账）
        const params = {
          page: this.listQuery.current - 1, // 后端从0开始，前端从1开始
          size: this.listQuery.pageSize
        }
        
        // 添加查询条件
        if (this.listQuery.contractName) {
          params.contractName = this.listQuery.contractName
        }
        if (this.listQuery.ledgerTitle) {
          params.remark = this.listQuery.ledgerTitle
        }
        if (this.listQuery.demandUnitName) {
          params.demandUnitName = this.listQuery.demandUnitName
        }
        
        const response = await query_fund_ledgers_pagination(params)
        
        // 处理响应数据
        if (response.data && response.data.success) {
          const responseData = response.data.data
          
          // 适配不同的响应结构
          let list = []
          if (responseData.content) {
            list = responseData.content
          } else if (responseData.data) {
            list = responseData.data
          } else if (Array.isArray(responseData)) {
            list = responseData
          }
          
          // 转换数据格式：台账响应子项为 paymentRecords（新接口）
          this.list = list.map(item => {
            const records = item.paymentRecords || item.fundLedgerItems || []
            const itemCount = item.paymentRecordCount != null ? item.paymentRecordCount : records.length
            const totalAmount = item.paymentTotalAmount != null
              ? parseFloat(item.paymentTotalAmount)
              : records.reduce((sum, it) => sum + parseFloat(it.amount || 0), 0)
            const paymentDates = records.map(it => it.paymentDate || it.payment_date).filter(Boolean).sort()
            const firstPaymentDate = item.firstPaymentDate || (paymentDates.length > 0 ? paymentDates[0] : null)
            const lastPaymentDate = item.lastPaymentDate || (paymentDates.length > 0 ? paymentDates[paymentDates.length - 1] : null)
            return {
              id: item.id,
              ledger_no: item.ledgerNumber || item.ledger_number,
              ledger_title: item.remark || item.ledgerTitle || item.ledger_title || '-',
              demand_unit_names: item.demandUnitNames || item.demand_unit_names || '-',
              contract_id: item.contractId || item.contract_id,
              contract_name: item.contractName || item.contract_name || '-',
              item_count: itemCount,
              total_amount: totalAmount,
              first_payment_date: firstPaymentDate,
              last_payment_date: lastPaymentDate,
              create_time: item.createTime || item.create_time
            }
          })
          
          // 设置总数
          this.total = responseData.totalElements || responseData.total || 0
          
          // 用于合计行的数据（当前页数据，如果需要所有数据需要调用不分页接口）
          this.allFilteredData = [...this.list]
        } else {
          this.list = []
          this.total = 0
          this.allFilteredData = []
        }
      } catch (error) {
        console.error('查询台账列表失败:', error)
        this.$message.error('查询台账列表失败: ' + (error.response?.data?.message || error.message || '未知错误'))
        this.list = []
        this.total = 0
        this.allFilteredData = []
      } finally {
        this.listLoading = false
      }
    },
    
    // 计算合计行（使用所有过滤后的数据，不只是当前页）
    getSummaries(param) {
      const { columns } = param
      const sums = []
      // 使用所有过滤后的数据计算合计，而不是当前页的数据
      const data = this.allFilteredData || []
      
      columns.forEach((column, index) => {
        if (index === 0) {
          sums[index] = '合计'
          return
        }
        
        // 只对数值类型的列计算合计
        if (column.property === 'total_amount') {
          const values = data.map(item => {
            const value = Number(item[column.property])
            return isNaN(value) ? 0 : value
          })
          
          const sum = values.reduce((prev, curr) => prev + curr, 0)
          sums[index] = this.formatAmount(sum)
        } else if (column.label === '收付款笔数') {
          const totalCount = data.reduce((sum, item) => sum + (item.item_count || 0), 0)
          sums[index] = totalCount
        } else {
          sums[index] = ''
        }
      })
      
      return sums
    },
    
    // 格式化金额
    formatAmount(amount) {
      if (!amount) return '0.00'
      return parseFloat(amount).toFixed(2).replace(/\B(?=(\d{3})+(?!\d))/g, ',')
    },
    
    // 格式化日期
    formatDate(dateString) {
      if (!dateString) return '-'
      const date = new Date(dateString)
      return date.toLocaleString('zh-CN', {
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
        hour: '2-digit',
        minute: '2-digit'
      }).replace(/\//g, '-')
    },
    
    // 搜索
    handleFilter() {
      this.listQuery.current = 1
      this.getList()
    },
    
    // 新增
    handleCreate() {
      this.$router.push({ path: '/finance/finance-edit' })
    },
    
    // 编辑
    handleUpdate(row) {
      const id = row.id
      if (!id) {
        this.$message.warning('无法获取台账ID')
        return
      }
      this.$router.push({ path: `/finance/finance-edit/${id}` })
    },
    
    // 查看
    handleView(row) {
      const id = row.id
      if (!id) {
        this.$message.warning('无法获取台账ID')
        return
      }
      this.$router.push({ path: `/finance/finance-detail/${id}` })
    },
    // 流水查看
    handleViewFlows(row) {
      const id = row.id
      if (!id) {
        this.$message.warning('无法获取台账ID')
        return
      }
      this.$router.push({ path: `/finance/ledger-flows/${id}` })
    },
    
    // 删除
    handleDelete(row) {
      const id = row.id
      if (!id) {
        this.$message.warning('无法获取台账ID')
        return
      }
      this.$confirm('确认删除该收支台账?', '提示', {
        confirmButtonText: '确定',
        cancelButtonText: '取消',
        type: 'warning'
      }).then(async () => {
        try {
          await delete_fund_ledger(id)
          this.$message.success('删除成功!')
          // 重新加载列表
          this.getList()
        } catch (error) {
          console.error('删除台账失败:', error)
          this.$message.error('删除失败: ' + (error.response?.data?.message || error.message || '未知错误'))
        }
      }).catch(() => {
        this.$message({
          type: 'info',
          message: '已取消删除'
        })
      })
    },
    
    
  }
}
</script>

<style lang="scss" scoped>
.filter-container {
  padding: 15px;
  background: #fff;
  border-radius: 4px;
  margin-bottom: 20px;
}

.filter-item {
  margin-bottom: 10px;
  margin-right: 10px;
}

.empty-text {
  padding: 30px 0;
  text-align: center;
  color: #909399;
}

.attachment-list {
  margin-top: 10px;
}


// 合计行样式
::v-deep .el-table__footer-wrapper {
  .el-table__footer {
    .el-table__cell {
      background-color: #f5f7fa !important;
      font-weight: bold;
      color: #303133;
    }
  }
}
</style>
